import { type ReactNode, useEffect, useId, useRef } from "react";

/** The class names the route renders with, so one renderer can wear two palettes. */
export type HowMapClasses = {
  /** the wrapper (`drawn` is added to it once the route is in) */
  map: string;
  /** the absolutely positioned SVG behind the cards */
  path: string;
  /** the grid of steps */
  steps: string;
  /** one step's card (its first child is the numbered `.hn` badge) */
  step: string;
};

/** The marketing site's How it works page (`#site .hmap …` in site.css). */
export const SITE_HOWMAP: HowMapClasses = {
  map: "hmap",
  path: "hpath",
  steps: "hsteps",
  step: "hstep",
};

/** Onboarding's welcome screen (`#onb .omap …`, v46), in onboarding's own palette. */
export const ONB_HOWMAP: HowMapClasses = {
  map: "omap",
  path: "opath",
  steps: "osteps",
  step: "ostep",
};

export type HowMapProps = {
  /** each step's card content (icon, title, text); the numbered badge is added */
  steps: ReactNode[];
  /** the class set, `SITE_HOWMAP` (default) or `ONB_HOWMAP` */
  classes?: HowMapClasses;
  /** ids for the wrapper and the SVG (the prototype's `hmap`/`hpath`, `omap`/`opath`) */
  id?: string;
  svgId?: string;
  /**
   * `"inView"` (default) draws the route in when it first scrolls into view (How it works);
   * `"mount"` draws it in as soon as it is laid out, again whenever `replayKey` changes (the
   * onboarding welcome screen draws in each time it opens); `"none"` shows it finished.
   * `prefers-reduced-motion` always gets the finished route.
   */
  animate?: "inView" | "mount" | "none";
  replayKey?: unknown;
};

/**
 * Numbered checkpoints on a dotted route (the prototype's hiwPath, v32; v47's leg below the row).
 * How it works draws it, and onboarding's welcome screen (v46) uses the same renderer.
 *
 * The path is measured from where the badges actually land, so it survives the three-, two- and
 * one-column layouts: badges are grouped into rows, rows are joined through the side channels
 * (the crossing leg runs below the row's cards, so the route goes round the last card rather
 * than behind it), every corner is rounded, and a single column is a straight line down. It draws
 * in along its own length (a wide white line inside an SVG mask sweeps the dotted path into view,
 * so the dots appear rather than travel), each checkpoint lights up as the route reaches it, and
 * a flag marks the end. It redraws, finished, on resize.
 */
export function HowMap({
  steps,
  classes = SITE_HOWMAP,
  id,
  svgId,
  animate = "inView",
  replayKey,
}: HowMapProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const maskId = `hmask${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  useEffect(() => {
    const wrap = wrapRef.current;
    const svg = svgRef.current;
    if (!wrap || !svg) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    let raf = 0;
    const draw = (animateIn: boolean) => {
      cancelAnimationFrame(raf);
      const cards = [...wrap.querySelectorAll<HTMLElement>(`.${classes.step}`)];
      const badges = cards.map((c) => c.querySelector<HTMLElement>(".hn")!);
      const W = wrap.getBoundingClientRect();
      if (!badges.length || badges.some((b) => !b) || !W.width) return;
      const pts = badges.map((b, i) => {
        const r = b.getBoundingClientRect();
        return {
          x: r.left - W.left + r.width / 2,
          y: r.top - W.top + r.height / 2,
          bot: cards[i].getBoundingClientRect().bottom - W.top,
        };
      });
      const route = mapRoute(pts, W.width);
      const { d, end } = route;
      svg.innerHTML = `<defs><mask id="${maskId}" maskUnits="userSpaceOnUse">
          <path class="hmaskline" d="${d}"/></mask></defs>
        <path class="hdot" d="${d}" mask="url(#${maskId})"/>
        <g class="hflag" transform="translate(${(end.x + 40).toFixed(1)},${(end.y + 6).toFixed(1)}) scale(1.35)">
          <path d="M0 11 L0 -16" stroke="#E8A90B" stroke-width="2.2" stroke-linecap="round"/>
          <path d="M0 -15 L17 -9.5 L0 -4 Z" fill="#E8A90B"/>
          <ellipse cx="0" cy="12" rx="5" ry="2.1" fill="#E8A90B" opacity=".45"/></g>`;
      const line = svg.querySelector<SVGPathElement>(".hmaskline");
      if (!line?.getTotalLength) return;
      const L = line.getTotalLength();
      line.style.strokeDasharray = `${L} ${L}`;
      if (!animateIn || reduce) {
        line.style.transition = "none";
        line.style.strokeDashoffset = "0";
        wrap.classList.add("drawn");
        badges.forEach((b) => (b.style.transitionDelay = "0ms"));
        return;
      }
      // each checkpoint lights up as the route reaches it
      const delays = checkpointDelays(route.poly, pts, DRAW_MS);
      badges.forEach((b, i) => (b.style.transitionDelay = `${delays[i]}ms`));
      wrap.classList.remove("drawn");
      line.style.transition = "none";
      line.style.strokeDashoffset = String(L);
      void line.getBoundingClientRect();
      raf = requestAnimationFrame(() => {
        line.style.transition = `stroke-dashoffset ${DRAW_MS}ms cubic-bezier(.36,.06,.2,1)`;
        line.style.strokeDashoffset = "0";
        wrap.classList.add("drawn");
      });
    };

    let io: IntersectionObserver | null = null;
    let start: ReturnType<typeof setTimeout> | undefined;
    if (animate === "none" || reduce) {
      draw(false);
    } else if (animate === "mount") {
      // the prototype waits a beat for the screen to lay out before drawing in
      wrap.classList.remove("drawn");
      start = setTimeout(() => draw(true), 60);
    } else if ("IntersectionObserver" in window) {
      // Laid out but hidden until it comes into view, then drawn in.
      draw(false);
      wrap.classList.remove("drawn");
      const line = svg.querySelector<SVGPathElement>(".hmaskline");
      if (line?.getTotalLength) {
        line.style.transition = "none";
        line.style.strokeDashoffset = String(line.getTotalLength());
      }
      io = new IntersectionObserver(
        (es) => {
          if (!es.some((e) => e.isIntersecting)) return;
          io?.disconnect();
          io = null;
          draw(true);
        },
        { threshold: 0.2 },
      );
      io.observe(wrap);
    } else {
      draw(false);
    }

    let t: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (wrap.offsetParent) draw(false);
      }, 200);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      clearTimeout(start);
      io?.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [maskId, classes.step, animate, replayKey]);

  return (
    <div className={classes.map} id={id} ref={wrapRef}>
      <svg className={classes.path} id={svgId} ref={svgRef} aria-hidden="true" focusable="false" />
      <div className={classes.steps}>
        {steps.map((s, i) => (
          <div className={classes.step} key={i}>
            <span className="hn" aria-hidden="true">
              {i + 1}
            </span>
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}

/** How long the route takes to draw in, ms. */
export const DRAW_MS = 1900;

type Pt = { x: number; y: number };
/** A badge centre, with the bottom of its card when known. */
type Checkpoint = Pt & { bot?: number };

/**
 * The route through the badge centres `pts` (in reading order) inside a box `width` wide: rows
 * joined through the side channels, every corner rounded, one column a straight line down.
 * Returns the polyline, its SVG path and the end point.
 *
 * The leg that crosses from one row to the next runs below the row's cards (`bot`), in the open
 * channel above the next row's badges (v47). Until v47 it ran at the midpoint of the two badge
 * centres, halfway down the cards above, so it was hidden behind them.
 */
export function mapRoute(pts: Checkpoint[], width: number): { poly: Pt[]; d: string; end: Pt } {
  const rows: Checkpoint[][] = [];
  pts.forEach((p) => {
    const row = rows.find((r) => Math.abs(r[0].y - p.y) < 6);
    if (row) row.push(p);
    else rows.push([p]);
  });
  const PAD = Math.min(30, width * 0.035);
  const poly: Pt[] = [];
  rows.forEach((row, ri) => {
    row.forEach((p) => poly.push({ x: p.x, y: p.y }));
    const next = rows[ri + 1];
    if (!next) return;
    const a = row[row.length - 1];
    const b = next[0];
    if (Math.abs(a.x - b.x) < 8) {
      // one column: straight down
      poly.push({ x: b.x, y: b.y - 0.01 });
      return;
    }
    const rowBot = Math.max(...row.map((p) => p.bot ?? p.y));
    const mid = Math.min(Math.max(rowBot + 14, (rowBot + b.y) / 2), b.y - 14);
    const right = width - PAD;
    poly.push({ x: right, y: a.y }, { x: right, y: mid }, { x: PAD, y: mid }, { x: PAD, y: b.y });
  });
  // round every corner so the route curves rather than hinges
  const R = 20;
  const f = (v: number) => v.toFixed(1);
  let d = `M${f(poly[0].x)} ${f(poly[0].y)}`;
  for (let i = 1; i < poly.length; i++) {
    const p = poly[i];
    const n = poly[i + 1];
    if (!n) {
      d += ` L${f(p.x)} ${f(p.y)}`;
      break;
    }
    const a = poly[i - 1];
    const d1 = Math.hypot(p.x - a.x, p.y - a.y);
    const d2 = Math.hypot(n.x - p.x, n.y - p.y);
    const r = Math.min(R, d1 / 2, d2 / 2);
    if (r < 3) {
      d += ` L${f(p.x)} ${f(p.y)}`;
      continue;
    }
    const e1 = { x: p.x + ((a.x - p.x) / d1) * r, y: p.y + ((a.y - p.y) / d1) * r };
    const e2 = { x: p.x + ((n.x - p.x) / d2) * r, y: p.y + ((n.y - p.y) / d2) * r };
    d += ` L${f(e1.x)} ${f(e1.y)} Q${f(p.x)} ${f(p.y)} ${f(e2.x)} ${f(e2.y)}`;
  }
  return { poly, d, end: poly[poly.length - 1] };
}

/** When each checkpoint lights up, ms: the share of the route's length before it reaches it. */
export function checkpointDelays(poly: Pt[], pts: Pt[], ms: number): number[] {
  let acc = 0;
  const cum = [0];
  for (let i = 1; i < poly.length; i++) {
    acc += Math.hypot(poly[i].x - poly[i - 1].x, poly[i].y - poly[i - 1].y);
    cum.push(acc);
  }
  let pi = 0;
  return pts.map((pt, i) => {
    while (
      pi < poly.length &&
      !(Math.abs(poly[pi].x - pt.x) < 1 && Math.abs(poly[pi].y - pt.y) < 1)
    ) {
      pi++;
    }
    const at = pi < poly.length && acc ? cum[pi] / acc : i / pts.length;
    return Math.round(at * ms * 0.92);
  });
}
